import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  HStack,
  Divider,
  Flex,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import TaxIdSelect from '../../common/TaxIdSelect';
import CustomToast from '../../../common/CustomToast';
import { TAX_ID_VALIDATION_PATTERNS } from '../../../utils/taxIdValidation';

const TaxIdEditModal = ({
  isOpen,
  onClose,
  currentTaxId,
  onSubmit,
  onRemove,
  isLoading = false,
}) => {
  const [taxIdData, setTaxIdData] = useState({ type: '', value: '' });
  const [taxIdValidation, setTaxIdValidation] = useState({
    isValid: null,
    message: '',
    hasBlockingErrors: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (currentTaxId) {
      setTaxIdData({
        type: currentTaxId.type || '',
        value: currentTaxId.value || '',
      });
    } else {
      setTaxIdData({ type: '', value: '' });
    }
    setTaxIdValidation({
      isValid: null,
      message: '',
      hasBlockingErrors: false,
    });
  }, [currentTaxId, isOpen]);

  const handleTaxIdChange = (newTaxIdData) => {
    setTaxIdData(newTaxIdData);
  };

  const handleTaxIdValidationChange = (validationResult) => {
    setTaxIdValidation(validationResult);
  };

  const handleSubmit = async () => {
    if (taxIdData.value && taxIdValidation.hasBlockingErrors) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Invalid Tax ID"
            description="Please enter a valid tax ID format."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
      return;
    }

    if (taxIdData.value && !taxIdData.type) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Missing Tax ID Type"
            description="Please select a tax ID type."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
      return;
    }

    if (taxIdData.type && !taxIdData.value) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Missing Tax ID Value"
            description="Please enter a tax ID value."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
      return;
    }

    try {
      setIsSubmitting(true);

      if (taxIdData.type && taxIdData.value) {
        await onSubmit(taxIdData.type, taxIdData.value);
        toast({
          render: (props) => (
            <CustomToast
              status="success"
              title="Tax ID Updated"
              description="Your tax ID has been updated successfully."
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      }

      onClose();
    } catch (error) {
      console.error('Error updating tax ID:', error);

      let showTypeHelp = false;
      let typeKey = taxIdData.type;

      if (error.code === 'tax_id_invalid') {
        showTypeHelp = true;
      } else if (error.message && error.message.includes('Invalid value for')) {
        const match = error.message.match(/Invalid value for ([a-z_]+)/i);
        if (match && match[1]) {
          typeKey = match[1];
          showTypeHelp = true;
        }
      }

      if (showTypeHelp) {
        const patternInfo = TAX_ID_VALIDATION_PATTERNS[typeKey];
        const helpMsg =
          patternInfo?.message ||
          'The tax ID format is incorrect. Please check the example format and try again.';
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Invalid Tax ID"
              description={helpMsg}
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      } else {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Error"
              description={
                error.message || 'Failed to update tax ID. Please try again.'
              }
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async () => {
    try {
      setIsSubmitting(true);
      await onRemove();
      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Tax ID Removed"
            description="Your tax ID has been removed successfully."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
      onClose();
    } catch (error) {
      console.error('Error removing tax ID:', error);
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Error"
            description="Failed to remove tax ID. Please try again."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = () => {
    if (!currentTaxId) {
      return taxIdData.type && taxIdData.value;
    }
    return (
      taxIdData.type !== currentTaxId.type ||
      taxIdData.value !== currentTaxId.value
    );
  };

  const canSubmit = () => {
    if (!taxIdData.type || !taxIdData.value) return false;
    if (taxIdData.value && taxIdValidation.isValid === false) return false;
    return hasChanges();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent py={8} px={6}>
        <ModalCloseButton />
        <Text textStyle="heading4" textAlign="center">
          {currentTaxId ? 'Edit tax ID' : 'Add tax ID (optional)'}
        </Text>
        <Divider my="10px" />
        <ModalBody>
          <VStack spacing={4}>
            <Text textStyle="bodyMedium">
              Your Tax ID (such as a VAT number) is used to calculate and apply
              the correct taxes to your invoices.
            </Text>
            <Text textStyle="bodyMedium">
              VAT is determined based on your billing address, not just your Tax
              ID.
            </Text>

            {taxIdValidation.validationLevel === 'informational' &&
              taxIdValidation.disclaimer && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">{taxIdValidation.disclaimer}</Text>
                </Alert>
              )}

            <TaxIdSelect
              type={taxIdData.type}
              value={taxIdData.value}
              onChange={handleTaxIdChange}
              onValidationChange={handleTaxIdValidationChange}
            />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Flex direction="column" w="100%" gap={4}>
            {currentTaxId && (
              <Button
                leftIcon={<DeleteIcon />}
                variant="outline"
                colorScheme="red"
                size="sm"
                onClick={handleRemove}
                isLoading={isSubmitting}
                mr="auto"
                w="100%"
              >
                Remove
              </Button>
            )}

            <Button
              onClick={handleSubmit}
              isLoading={isSubmitting}
              w="100%"
              isDisabled={!canSubmit()}
            >
              Continue
            </Button>

            <Button
              variant="link"
              onClick={onClose}
              isDisabled={isSubmitting}
              w="100%"
            >
              <Text textStyle="bodySmall">Cancel</Text>
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaxIdEditModal;
